import React, { useState } from 'react'
import type { Feedback as FeedbackData, FfpDashboardData, PeriodKey } from '../../types'
import { CHANNEL_WORDS, periodRatingLead, sincePhrase } from '../../utils'
import { AboutPanel } from '../AboutPanel'
import { Reveal } from '../Reveal'
import { Intro, SectionHeader } from '../SectionHeader'
import { CommentsCard } from './CommentsCard'
import styles from './feedback.module.sass'
import { FEEDBACK_PANEL_TITLE, FeedbackPanel } from './FeedbackPanel'
import { RatingsCard } from './RatingsCard'
import { commentsView, CommentsView, feedbackMode, FeedbackMode, ratedChannels } from './utils'

type Props = { data: FfpDashboardData; period: PeriodKey }

// Ratings and comments always cover the whole programme; a period with too
// few reviews of its own says so.
export const Feedback = ({ data, period }: Props) => {
  const [panelOpen, setPanelOpen] = useState(false)
  const { feedback } = data
  const mode = feedbackMode(feedback)
  const view = commentsView(feedback.comments, feedback.keywords.length)

  const openPanel = () => setPanelOpen(true)
  const closePanel = () => setPanelOpen(false)

  return (
    <section id="feedback" className={styles.section}>
      <Reveal>
        <SectionHeader title="What your team thinks about the programme" intro={feedbackIntro(feedback, mode, view, period)} />
      </Reveal>
      <Reveal>
        <RatingsCard data={data} period={period} mode={mode} view={view} since={sincePhrase(data.first_month)} highlighted={panelOpen} onAbout={openPanel} />
      </Reveal>
      <Comments comments={feedback.comments} mode={mode} />
      <AboutPanel open={panelOpen} title={FEEDBACK_PANEL_TITLE} onClose={closePanel}>
        <FeedbackPanel feedback={feedback} mode={mode} view={view} />
      </AboutPanel>
    </section>
  )
}

// Comments only exist once there are enough reviews to show ratings.
const Comments = ({ comments, mode }: { comments: FeedbackData['comments']; mode: FeedbackMode }) => {
  if (mode !== 'ratings') return null

  return (
    <Reveal>
      <CommentsCard comments={comments} />
    </Reveal>
  )
}

const feedbackIntro = (feedback: FeedbackData, mode: FeedbackMode, view: CommentsView, period: PeriodKey): Intro => {
  if (mode === 'none') return { before: 'Your people ', highlight: 'haven’t rated the programme yet', after: '.' }
  if (mode === 'hidden') {
    const minimum = feedback.minimum_reviews
    return { before: 'Ratings and comments are ', highlight: `hidden until ${minimum} people`, after: ` have reviewed coaching, or ${minimum} have reviewed workshops.` }
  }

  const rated = ratedChannels(feedback)
  const subject = rated.length === 1 ? CHANNEL_WORDS[rated[0]].name.toLowerCase() : 'the programme'
  const average = (feedback.average ?? 0).toFixed(1)
  const lead = periodRatingLead(feedback, period)

  if (lead && feedback.periods[period].reviews === 0) {
    return { before: '', highlight: lead, after: `, but your people rate ${subject} ${average} out of 5 since the start.` }
  }
  if (lead) {
    return { before: '', highlight: lead, after: `, hidden for now. Since the start, your people rate ${subject} ${average} out of 5.` }
  }

  const after = view === 'preparing' ? '. Their comments are being prepared.' : '.'
  return { before: `Your people rate ${subject} `, highlight: `${average} out of 5`, after }
}
